import { useEffect, useState } from "react";
import { Download, X, Share2 } from "lucide-react";

const DISMISS_KEY = "ssc_install_dismissed_until";
const DISMISS_DAYS = 14;

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    return v && Date.now() < Number(v);
  } catch {
    return false;
  }
}

function dismiss() {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + DISMISS_DAYS * 24 * 3600 * 1000),
    );
  } catch { /* noop */ }
}

export default function InstallPrompt() {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Chrome / Edge / Android — captures install opportunity
    const onBip = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      // Wait a moment so it doesn't slam on first load
      setTimeout(() => setShow(true), 6000);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari — no beforeinstallprompt; surface manual instructions after a delay
    if (isIos()) {
      setTimeout(() => setShow(true), 8000);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  if (!show || isStandalone()) return null;

  async function install() {
    if (deferredEvt) {
      deferredEvt.prompt();
      const choice = await deferredEvt.userChoice;
      if (choice?.outcome === "accepted") {
        setShow(false);
      } else {
        dismiss();
        setShow(false);
      }
      setDeferredEvt(null);
    } else if (isIos()) {
      setShowIosHelp(true);
    }
  }

  function close() {
    dismiss();
    setShow(false);
    setShowIosHelp(false);
  }

  return (
    <>
      <div
        className="fixed left-3 right-3 bottom-3 z-[70] md:left-auto md:right-6 md:bottom-6 md:max-w-md"
        role="dialog"
        aria-label="Install Scott Sells Chevys app"
        data-testid="install-prompt"
      >
        <div className="rounded-2xl bg-[var(--ssc-navy)] text-white shadow-2xl border border-white/10 p-4 flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-[var(--ssc-blue)] flex items-center justify-center">
            <Download size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold leading-tight">Install Scott Sells Chevys</div>
            <p className="text-xs text-[#cdd9ef] mt-1 leading-snug">
              Add the app to your phone — one tap to text Scott, preview a deal in Build My Own Deal, or value your trade. No app store required.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={install}
                className="ssc-btn ssc-btn-primary text-sm px-4"
                data-testid="install-prompt-install"
              >
                <Download size={14} /> Install
              </button>
              <button
                type="button"
                onClick={close}
                className="text-xs font-bold text-[#9bb7ff] hover:text-white px-2"
                data-testid="install-prompt-dismiss"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="shrink-0 text-[#9bb7ff] hover:text-white"
            aria-label="Close install prompt"
            data-testid="install-prompt-close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {showIosHelp && (
        <div
          className="fixed inset-0 z-[90] bg-black/60 flex items-end md:items-center justify-center p-4"
          onClick={close}
          data-testid="install-ios-help"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-xl font-black text-[var(--ssc-navy)]">Install on iPhone / iPad</h3>
              <button
                type="button"
                onClick={close}
                className="text-[#6b7c93] hover:text-[var(--ssc-navy)]"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <ol className="mt-3 space-y-2 text-sm text-[var(--ssc-ink)] leading-relaxed">
              <li className="flex gap-2">
                <span className="font-black text-[var(--ssc-blue)]">1.</span>
                <span>Tap the <strong>Share</strong> <Share2 size={14} className="inline -mt-0.5" /> icon in Safari's bottom bar.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-black text-[var(--ssc-blue)]">2.</span>
                <span>Scroll down and tap <strong>"Add to Home Screen"</strong>.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-black text-[var(--ssc-blue)]">3.</span>
                <span>Tap <strong>Add</strong> in the top-right. Done — Scott is now an app on your phone.</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={close}
              className="ssc-btn ssc-btn-primary text-sm mt-5 w-full"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
